import { BadgeDollarSign, Percent, RefreshCw, Settings2, ShieldCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { getPaymentSettings, updatePaymentSettings } from "../../lib/api";
import type { PaymentSettings } from "../../lib/types";
import { FormPortalHeader } from "./FormPortalHeader";

export function StripePaymentSettingsPage() {
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Stripe Payment Settings | Kreatethedate Forms Portal";
    void load();
  }, []);

  async function load() {
    setError("");
    try {
      const data = await getPaymentSettings();
      setSettings(data.settings);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load payment settings.");
    }
  }

  async function save() {
    if (!settings) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await updatePaymentSettings(settings);
      setSettings(result.settings);
      setMessage("Stripe payment settings updated.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to update payment settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="command-center-shell min-h-screen text-[#fff9ef]">
      <FormPortalHeader />
      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
        <div className="relative mx-auto grid max-w-6xl gap-5">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FF6A2B]/35 px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF6A2B]">
            <Settings2 className="size-4" />
            Stripe Payment Settings
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Payment, tax, and invoice controls</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">
            Configure the Forms Portal payment engine, tax behavior, and payment plan preferences without touching Memory Media Vault.
          </p>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-5xl gap-6">
          {error ? <Notice tone="error" message={error} /> : null}
          {message ? <Notice tone="success" message={message} /> : null}
          {settings ? (
            <section id="payment-link-readiness" className="command-center-card grid gap-6 rounded-lg p-5 sm:p-6">
              <div id="tax-settings" className="grid gap-4 md:grid-cols-2">
                <SettingField
                  icon={Percent}
                  label="Tax Percentage"
                  value={String(settings.taxPercentage)}
                  onChange={(value) => setSettings({ ...settings, taxPercentage: Number(value || 0) })}
                />
                <SettingField
                  icon={BadgeDollarSign}
                  label="Tax Label"
                  value={settings.taxLabel}
                  onChange={(value) => setSettings({ ...settings, taxLabel: value })}
                />
                <ToggleField
                  label="Tax Enabled"
                  checked={settings.taxEnabled}
                  onChange={(checked) => setSettings({ ...settings, taxEnabled: checked })}
                />
                <div id="payment-plan-defaults">
                  <SettingField
                    icon={ShieldCheck}
                    label="Default Payment Mode"
                    value={settings.defaultPaymentMode}
                    onChange={(value) =>
                      setSettings({
                        ...settings,
                        defaultPaymentMode: value as PaymentSettings["defaultPaymentMode"],
                      })
                    }
                    placeholder="deposit | full | custom"
                  />
                </div>
                <div>
                  <ToggleField
                    label="Allow Deposits"
                    checked={settings.allowDeposits}
                    onChange={(checked) => setSettings({ ...settings, allowDeposits: checked })}
                  />
                </div>
                <div>
                  <ToggleField
                    label="Allow Full Payment"
                    checked={settings.allowFullPayment}
                    onChange={(checked) => setSettings({ ...settings, allowFullPayment: checked })}
                  />
                </div>
                <div>
                  <ToggleField
                    label="Allow Custom Schedules"
                    checked={settings.allowCustomSchedules}
                    onChange={(checked) => setSettings({ ...settings, allowCustomSchedules: checked })}
                  />
                </div>
              </div>

              <div id="invoice-behavior" className="rounded-lg border border-[#FF6A2B]/18 bg-black/20 p-4 text-sm font-semibold text-[#fff9ef]/72">
                Invoice behavior follows the selected payment mode, tax settings, and deposit rules above.
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={saving}
                className="admin-primary-button focus-ring disabled:opacity-60"
              >
                  <RefreshCw className="size-4" />
                  {saving ? "Saving..." : "Save Payment Settings"}
                </button>
                <button
                  type="button"
                  onClick={() => void load()}
                className="admin-secondary-button focus-ring"
              >
                  Refresh
                </button>
              </div>
            </section>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function SettingField({
  icon: Icon,
  label,
  value,
  onChange,
  placeholder,
  id,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
}) {
  return (
    <label id={id} className="grid gap-2 text-sm font-bold text-[#fff9ef]">
      <span className="flex items-center gap-2">
        <Icon className="size-4 text-[#FF6A2B]" />
        {label}
      </span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="command-center-field focus-ring min-h-12 rounded-lg px-4 text-sm font-semibold"
      />
    </label>
  );
}

function ToggleField({
  label,
  checked,
  onChange,
  id,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
}) {
  return (
    <label id={id} className="flex items-center justify-between gap-4 rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4 text-sm font-bold text-[#fff9ef]">
      {label}
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="size-5 accent-[#FF6A2B]" />
    </label>
  );
}

function Notice({ tone, message }: { tone: "error" | "success"; message: string }) {
  const classes =
    tone === "error"
      ? "border-red-300 bg-red-50 text-red-800"
      : "border-[#FF6A2B]/30 bg-white/5 text-[#fff9ef]";
  return <p className={`rounded-lg border px-5 py-4 text-sm font-bold ${classes}`}>{message}</p>;
}
